import { useState, useMemo, useEffect} from "react";
import MoveProductPanel from "./moveproductpanel";
import { api } from "../../services/api";

function ProductTree({ initialProducts }) {
  const [products, setProducts] = useState(initialProducts || []);
  const [search, setSearch] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [collapsedCompanies, setCollapsedCompanies] = useState({});
  const [collapsedLines, setCollapsedLines] = useState({});
  const [loading, setLoading] = useState(true);

  const filteredProducts = useMemo(() => {
  if (!search) return products;

  return products.filter(p =>
    (p.Name || "").toLowerCase().includes(search.toLowerCase())
  );
}, [products, search]);

  const grouped = useMemo(() => {
    return filteredProducts.reduce((acc, p) => {
      if (!acc[p.Company]) acc[p.Company] = {};
      if (!acc[p.Company][p.Line]) acc[p.Company][p.Line] = [];
      acc[p.Company][p.Line].push(p);
      return acc;
    }, {});
  }, [filteredProducts]);
    const fetchProducts = async (signal) => {
  try {
    setLoading(true);

    const res = await api.get("/products", { signal });

    if (signal?.aborted) return;

    setProducts(res.data || []);
  } catch (err) {
    if (
      err.name === "CanceledError" ||
      err.code === "ERR_CANCELED" ||
      err.message === "canceled"
    ) {
      return;
    }

    console.error("Failed to fetch products", err);
  } finally {
    if (!signal?.aborted) {
      setLoading(false);
    }
  }
};
  useEffect(() => {
  const controller = new AbortController();

  fetchProducts(controller.signal);

  return () => {
    controller.abort();
  };
}, []);

  const refreshProducts = async () => {
  await fetchProducts();
};

  const toggleCompany = key =>
    setCollapsedCompanies(prev => ({ ...prev, [key]: !prev[key] }));

  const toggleLine = key =>
    setCollapsedLines(prev => ({ ...prev, [key]: !prev[key] }));

  return (
    <div className="max-w-full mx-auto bg-white rounded-xl shadow-lg h-full flex flex-col overflow-hidden">

      {/* Search */}
      <div className="p-6 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-slate-100 sticky top-0 z-30 flex justify-between items-center shadow-sm">
        <div className="relative flex-1 max-w-md">
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            disabled={loading}
            placeholder="Search products..."
            className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-400 focus:border-transparent shadow-sm hover:border-slate-400 transition-all"
          />
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-lg">🔍</span>
        </div>
        <div className="text-sm font-medium text-slate-600 bg-white px-4 py-2 rounded-lg shadow-sm">
          Showing <span className="font-bold text-cyan-600">{loading ? "..." : filteredProducts.length}</span> products
        </div>
      </div>

      {/* Header */}
      <div className="sticky top-[89px] bg-gradient-to-r from-slate-50 to-slate-100 z-20 border-b-2 border-slate-200 px-6 py-4 shadow-sm">
        <div className="flex gap-6 text-sm font-semibold text-slate-600 uppercase tracking-wide">
          <span className="ml-20 w-64">Product Name</span>
          <span className="w-32">Line</span>
          <span className="w-40">Supplier</span>
          <span className="w-32">Buy Price</span>
          <span className="w-32">Sell Price</span>
          <span>Barcode</span>
        </div>
      </div>


      {/* Scrollable Data */}
      <div className="flex-1 overflow-y-auto">

  {loading ? (
    <div className="flex items-center justify-center h-full">
      <div className="flex flex-col items-center gap-3">
        <div className="w-10 h-10 border-4 border-cyan-400 border-t-transparent rounded-full animate-spin"></div>
        <span className="text-slate-500 font-medium">Loading products...</span>
      </div>
    </div>
  ) : (
    Object.entries(grouped).map(([company, lines]) => (
      <div key={company} className="border-b border-slate-200">
        {/* your existing code unchanged */}
            {/* Company */}
            <div
              onClick={() => toggleCompany(company)}
              className="flex items-center px-6 py-3 bg-gradient-to-r from-slate-50 to-slate-100 cursor-pointer hover:from-cyan-50 hover:to-blue-50 transition-all duration-200 border-l-4 border-transparent hover:border-cyan-500"
            >
              <span
                className={`mr-3 transition-transform duration-200 text-cyan-600 ${
                  collapsedCompanies[company] ? "-rotate-90" : ""
                }`}
              >
                ▼
              </span>
              <h2 className="text-sm font-bold text-slate-700">{company}</h2>
            </div>

            {!collapsedCompanies[company] &&
              Object.entries(lines).map(([lineName, items]) => {
                const lineKey = `${company}-${lineName}`;
                return (
                  <div key={lineKey}>
                    {/* Line */}
                    <div
                      onClick={() => toggleLine(lineKey)}
                      className="flex items-center ml-8 px-6 py-2.5 cursor-pointer hover:bg-slate-50 transition-colors duration-150 border-l-2 border-transparent hover:border-blue-400"
                    >
                      <span
                        className={`mr-3 transition-transform duration-200 text-blue-500 text-sm ${
                          collapsedLines[lineKey] ? "-rotate-90" : ""
                        }`}
                      >
                        ▼
                      </span>
                      <h3 className="text-sm font-semibold text-slate-600">{lineName}</h3>
                    </div>

                    {!collapsedLines[lineKey] &&
                      items.map(p => (
                        <div
                         key={`${company}-${lineName}-${p.IdNumber}-${p.Barcode}`}
                          draggable
                          onDragStart={() => setSelectedProduct(p)}
                          className="px-6 py-3 border-b border-slate-100 hover:bg-gradient-to-r hover:from-cyan-50 hover:to-blue-50 cursor-grab active:cursor-grabbing transition-all duration-150 hover:shadow-sm"
                        >
                          <div className="flex items-center ml-12 gap-6 text-sm">
                            <span className="w-64 font-medium text-slate-800">{p.Name}</span>
                            <span className="w-32 text-slate-600">{p.Line}</span>
                            <span className="w-40 text-slate-600">{p.Supplier || "N/A"}</span>
                            <span className="w-32 text-slate-700 font-medium">{p.BuyPrice}</span>
                            <span className="w-32 text-slate-700 font-medium">{p.SellPrice}</span>
                            <span className="text-xs text-slate-500">{p.Barcode || "N/A"}</span>
                          </div>
                        </div>
                      ))}
                  </div>
                );
              })}
          </div>
           ))
  )}
</div>

      {/* Move Panel */}
      {selectedProduct && (
      <MoveProductPanel
        product={selectedProduct}
        allProducts={products}
        onClose={() => setSelectedProduct(null)}
        onProductMoved={refreshProducts} // <-- new prop
      />
    )}
    </div>
  );
}

export default ProductTree;
